import type { RecordFilterGroup } from "@scayla/api-client-fetch";
import { useCloned } from "@vueuse/core";
import { defineStore } from "pinia";
import { ref } from "vue";

export type ModuleCurrentFilter = Record<string, RecordFilterGroup>;

export enum FilterModuleKey {
  MODULE = "MODULE",
  MODULE_RECORD_FORM = "MODULE_RECORD_FORM",
  FILE_EXPLORER = "FILE_EXPLORER",
  FILE_VERSIONS = "FILE_VERSIONS",
  BOOKING = "BOOKING",
  PRODUCT_PRICE_SALE = "PRODUCT_PRICE_SALE",
  PRODUCT_PRICE_PURCHASE = "PRODUCT_PRICE_PURCHASE",
  PRODUCT_PRICE_RENT = "PRODUCT_PRICE_RENT",
  PRODUCT_PRICE_RENT_OUT = "PRODUCT_PRICE_RENT_OUT",
  TEMPLATES = "TEMPLATES",
  USERS = "USERS",
  PUBLIC_CATEGORIES = "PUBLIC_CATEGORIES",
  PRIVATE_CATEGORIES = "PRIVATE_CATEGORIES",
  CUSTOM_FIELDS = "CUSTOM_FIELDS",
  CUSTOM_SEQUENCES = "CUSTOM_SEQUENCES",
  TENANT_ACCOUNTS = "TENANT_ACCOUNTS",
  INTEGRATIONS = "INTEGRATIONS",
  ACCOUNTS = "ACCOUNTS",
  RESOURCES = "RESOURCES",
  LAYOUT_SECTION_GROUPS = "LAYOUT_SECTION_GROUPS",
  STATUS_SETS = "STATUS_SETS",
  PRIORITY_SETS = "PRIORITY_SETS",
  TENANTS = "TENANTS",
  FIELD_GROUPS = "FIELD_GROUPS",
  RECORD_STATUS_SETS = "RECORD_STATUS_SETS",
  RECORD_PRIORITY_SETS = "RECORD_PRIORITY_SETS",
  CUSTOM_FIELD_LOCATED_IN = "CUSTOM_FIELD_LOCATED_IN",
  ORGANIZATIONS = "ORGANIZATIONS",
  ROLES = "ROLES",
  USER_GROUPS = "USER_GROUPS",
  ASSIGNED_ROLES = "ASSIGNED_ROLES",
  USER_ASSIGNED_USER_GROUPS = "USER_ASSIGNED_USER_GROUPS",
  USER_INDIVIDUAL_PERMISSIONS = "USER_INDIVIDUAL_PERMISSIONS",
  SERVICE_INTEGRATION_ACCOUNTS = "SERVICE_INTEGRATION_ACCOUNTS",
}

export const useFilterStore = defineStore(
  "filterConfig",
  () => {
    const currentFilters = ref<ModuleCurrentFilter>({});
    const currentTableFilters = ref<Record<string, RecordFilterGroup>>({});
    const showFilterRowMap = ref<Record<string, boolean>>({});
    const showAdvancedFilterMap = ref<Record<string, boolean>>({});
    // Tracks modules where the user explicitly clicked a filterset (vs. auto-selected by activeByDefault).
    // Only user-explicitly-selected filtersets are restored on reload.
    const userSelectedFilters = ref<Record<string, boolean>>({});
    // Transient (not persisted): filterset to auto-select on next FiltersList initial load.
    // Set after filterset creation so the new filterset becomes selected when navigating back.
    const pendingFilterSelections = ref<Record<string, string>>({});

    function isUserSelected(moduleId: string): boolean {
      return !!userSelectedFilters.value[moduleId];
    }

    function markUserSelected(moduleId: string) {
      userSelectedFilters.value[moduleId] = true;
    }

    function clearUserSelected(moduleId: string) {
      delete userSelectedFilters.value[moduleId];
    }

    function setPendingFilterSelection(key: string, filterId: string) {
      pendingFilterSelections.value[key] = filterId;
    }

    function getPendingFilterSelection(key: string): string | undefined {
      return pendingFilterSelections.value[key];
    }

    function clearPendingFilterSelection(key: string) {
      delete pendingFilterSelections.value[key];
    }

    function setCurrentFilter(moduleId: string, filter: RecordFilterGroup) {
      currentFilters.value[moduleId] = useCloned(filter).cloned.value;
    }

    function getCurrentFilter(moduleId: string) {
      return currentFilters.value[moduleId] ? useCloned(currentFilters.value[moduleId]).cloned.value : undefined;
    }

    function removeCurrentFilter(moduleId: string) {
      delete currentFilters.value[moduleId];
    }

    function isFilterRowVisible(key: FilterModuleKey): boolean {
      if (!(key in showFilterRowMap.value)) return true;
      return showFilterRowMap.value[key];
    }

    function setShowFilterRow(key: FilterModuleKey, visible: boolean) {
      showFilterRowMap.value[key] = visible;
    }

    function toggleShowFilterRow(key: FilterModuleKey) {
      showFilterRowMap.value[key] = !showFilterRowMap.value[key];
    }

    function isAdvancedFilterOpen(key: FilterModuleKey): boolean {
      return !!showAdvancedFilterMap.value[key];
    }

    function setAdvancedFilterOpen(key: FilterModuleKey, visible: boolean) {
      showAdvancedFilterMap.value[key] = visible;
    }

    function setCurrentTableFilters(moduleId: string, filters: RecordFilterGroup) {
      currentTableFilters.value[moduleId] = useCloned(filters).cloned.value;
    }

    function getCurrentTableFilters(moduleId: string): RecordFilterGroup | undefined {
      return currentTableFilters.value[moduleId]
        ? useCloned(currentTableFilters.value[moduleId]).cloned.value
        : undefined;
    }

    return {
      currentFilters,
      setCurrentFilter,
      getCurrentFilter,
      removeCurrentFilter,
      userSelectedFilters,
      isUserSelected,
      markUserSelected,
      clearUserSelected,
      setPendingFilterSelection,
      getPendingFilterSelection,
      clearPendingFilterSelection,
      showFilterRowMap,
      isFilterRowVisible,
      setShowFilterRow,
      toggleShowFilterRow,
      showAdvancedFilterMap,
      isAdvancedFilterOpen,
      setAdvancedFilterOpen,
      currentTableFilters,
      setCurrentTableFilters,
      getCurrentTableFilters,
    };
  },
  {
    persist: {
      pick: ["currentFilters", "showFilterRowMap", "showAdvancedFilterMap"],
    },
  },
);
